import type { TFunction } from 'i18next';
import type { CsvColumn } from './useReportsCsvExport.types';
import { formatDateOnly, getLocalizedValue } from './useReportsCsvExport.utils';

const DIAGNOSTIC_PROCEDURES_EXPORT_LIMIT = 3;

const normalizeOptionKey = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, '');

const translateOptionValue = (
  value: string,
  labelsByKey: Record<string, string>,
): string => {
  const normalized = normalizeOptionKey(value);

  if (!normalized) {
    return '';
  }

  return labelsByKey[normalized] ?? value;
};

export const buildReportsCsvColumns = (
  t: TFunction,
  language: string,
): CsvColumn[] => {
  const yesLabel = t('reportCreation.step2.options.yesNo.yes');
  const noLabel = t('reportCreation.step2.options.yesNo.no');
  const animalTypeLabels: Record<string, string> = {
    vertebrate: t('reportCreation.step1.options.animalType.vertebrate'),
    invertebrate: t('reportCreation.step1.options.animalType.invertebrate'),
  };
  const wildTypeLabels: Record<string, string> = {
    freeroaming: t('reportCreation.step1.options.wildtype.freeRoaming'),
    captive: t('reportCreation.step1.options.wildtype.captive'),
    feral: t('reportCreation.step1.options.wildtype.feral'),
  };
  const surveillanceTypeLabels: Record<string, string> = {
    active: t('reportCreation.step2.options.surveillanceType.active'),
    passive: t('reportCreation.step2.options.surveillanceType.passive'),
    other: t('reportCreation.step2.options.surveillanceType.other'),
    unknown: t('reportCreation.step2.options.surveillanceType.unknown'),
  };
  const diagnosticCategoryLabels: Record<string, string> = {
    direct: t('reportCreation.step3.options.category.direct'),
    indirect: t('reportCreation.step3.options.category.indirect'),
    observation: t('reportCreation.step3.options.category.observation'),
    others: t('reportCreation.step3.options.category.others'),
  };

  const baseColumns: CsvColumn[] = [
    {
      header: t('reports.csvExport.headers.reportCode'),
      value: (report) => report.code,
    },
    {
      header: t('filters.fields.status'),
      value: (report) => report.status,
    },
    {
      header: t('filters.fields.country'),
      value: (report) => getLocalizedValue(report.country?.name, language),
    },
    {
      header: t('reportCreation.step1.fields.animalType'),
      value: (report) =>
        translateOptionValue(report.animalType ?? '', animalTypeLabels),
    },
    {
      header: t('reportCreation.step1.fields.species'),
      value: (report) => report.specie,
    },
    {
      header: t('reportCreation.step1.fields.wildtype'),
      value: (report) =>
        translateOptionValue(report.wildType ?? '', wildTypeLabels),
    },
    {
      header: t('pinPosition.latitude'),
      value: (report) => report.latitude,
    },
    {
      header: t('pinPosition.longitude'),
      value: (report) => report.longitude,
    },
    {
      header: t('reportCreation.step1.fields.inaccurateLocation'),
      value: (report) =>
        report.inaccurateLocation === undefined
          ? ''
          : report.inaccurateLocation
            ? yesLabel
            : noLabel,
    },
    {
      header: t('reportCreation.step1.fields.dateOfDetection'),
      value: (report) => formatDateOnly(report.detectionDate),
    },
    {
      header: t('reportCreation.step2.fields.surveillanceType'),
      value: (report) =>
        translateOptionValue(
          report.surveillanceType ?? '',
          surveillanceTypeLabels,
        ),
    },
    {
      header: t('reportCreation.step2.fields.description'),
      value: (report) => report.description,
    },
    {
      header: t('reportCreation.step2.fields.numberOfDeaths'),
      value: (report) => report.numberOfDeaths,
    },
    {
      header: t('reportCreation.step2.fields.numberOfSick'),
      value: (report) => report.numberOfSicks,
    },
    {
      header: t('reportCreation.step2.fields.populationSize'),
      value: (report) => report.populationSize,
    },
    {
      header: t('reportCreation.step2.fields.humanAffected'),
      value: (report) => report.humanAffected,
    },
    {
      header: t('reportCreation.step2.fields.domesticAnimalAffected'),
      value: (report) => report.domesticAnimalAffected,
    },
    {
      header: t('reportCreation.step2.fields.knowTheCause'),
      value: (report) =>
        report.knownCause === undefined
          ? ''
          : report.knownCause
            ? yesLabel
            : noLabel,
    },
    {
      header: t('reportCreation.versionNote.fieldLabel'),
      value: (report) => report.versionNote,
    },
    {
      header: t('reports.csvExport.headers.versionNumber'),
      value: (report) => report.versionNumber,
    },
    {
      header: t('reports.csvExport.headers.userCountry'),
      value: (report) =>
        report.user?.countryCode === report.country?.code
          ? getLocalizedValue(report.country?.name, language)
          : report.user?.countryCode,
    },
    {
      header: t('reports.csvExport.headers.userOrganization'),
      value: (report) => report.user?.organization,
    },
    {
      header: t('filters.fields.etiologyType'),
      value: (report) => getLocalizedValue(report.etiologyType?.name, language),
    },
    {
      header: t('filters.fields.etiologicalAgent'),
      value: (report) =>
        getLocalizedValue(report.etiologicalAgent?.name, language),
    },
    {
      header: t('reports.csvExport.headers.assets'),
      value: (report) => (report.assets?.length ? yesLabel : noLabel),
    },
  ];

  const diagnosticColumns: CsvColumn[] = [];

  for (let index = 0; index < DIAGNOSTIC_PROCEDURES_EXPORT_LIMIT; index += 1) {
    const displayIndex = index + 1;
    diagnosticColumns.push(
      {
        header: t('reports.csvExport.headers.diagnosticSampleType', {
          index: displayIndex,
        }),
        value: (report) =>
          getLocalizedValue(
            report.diagnosticProcedures?.[index]?.sampleType?.name,
            language,
          ),
      },
      {
        header: t('reports.csvExport.headers.diagnosticSubcategory', {
          index: displayIndex,
        }),
        value: (report) =>
          getLocalizedValue(
            report.diagnosticProcedures?.[index]?.subCategory?.name,
            language,
          ),
      },
      {
        header: t('reports.csvExport.headers.diagnosticCategoryOfTest', {
          index: displayIndex,
        }),
        value: (report) =>
          translateOptionValue(
            report.diagnosticProcedures?.[index]?.categoryOfTest ?? '',
            diagnosticCategoryLabels,
          ),
      },
      {
        header: t('reports.csvExport.headers.diagnosticFamily', {
          index: displayIndex,
        }),
        value: (report) =>
          getLocalizedValue(
            report.diagnosticProcedures?.[index]?.family?.name,
            language,
          ),
      },
      {
        header: t('reports.csvExport.headers.diagnosticExtraTestInformation', {
          index: displayIndex,
        }),
        value: (report) =>
          report.diagnosticProcedures?.[index]?.extraTestInformation,
      },
      {
        header: t('reports.csvExport.headers.diagnosticNumberOfNegativeTests', {
          index: displayIndex,
        }),
        value: (report) =>
          report.diagnosticProcedures?.[index]?.numberOfNegativeTests,
      },
      {
        header: t('reports.csvExport.headers.diagnosticNumberOfPositiveTests', {
          index: displayIndex,
        }),
        value: (report) =>
          report.diagnosticProcedures?.[index]?.numberOfPositiveTests,
      },
      {
        header: t('reports.csvExport.headers.diagnosticNumberOfTests', {
          index: displayIndex,
        }),
        value: (report) => report.diagnosticProcedures?.[index]?.numberOfTests,
      },
    );
  }

  return [...baseColumns, ...diagnosticColumns];
};
